import { Flex, Text } from "@fluentui/react-northstar";
import { TakingCommands, getQuestionFieldId } from "@teams/cards";
import { NominalQuestion, TeamsSurveyResponse } from "@teams/types";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";

import { useSurvey } from "../../../../_shared/actions/useSurvey";
import { TeamsLoader } from "../../../../_shared/components/TeamsLoader";
import { isError } from "../../../../_shared/services/ChannelApi";
import { surveyApi } from "../../../../_shared/services/SurveyApi";
import { useTeamsContext } from "../../../../_shared/state/TeamsContextProvider";
import { useInterval } from "../../../../_shared/utils/useInterval";
import { ProgressCountdownTimer } from "../ProgressCountdownTimer";
import { TriviaLeaderboard } from "./TriviaLeaderboard";
import { TriviaQuestionForm } from "./TriviaQuestionForm";
import { TriviaQuestionResults } from "./TriviaQuestionResults";

type Props = {
  surveyId: string;
  trackingSource: string;
};

export const TriviaTakingForm: React.FC<Props> = ({ surveyId, trackingSource }) => {
  const [userResponse, setUserResponse] = useState<TeamsSurveyResponse>();
  const [activeQuestion, setActiveQuestion] = useState<{
    question: NominalQuestion;
    index: number;
  }>();
  const { teamsContext } = useTeamsContext();
  const { survey, loadSurvey, isSurveyLoading, serverTime } = useSurvey();
  const clockSkew = useRef<number>(0);

  useEffect(() => {
    loadSurvey(teamsContext, surveyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const now = getNow();
    clockSkew.current = moment(serverTime).diff(now, "milliseconds");
  }, [serverTime]);

  useInterval(() => {
    if (!isSurveyLoading && survey?.state !== "closed") {
      loadSurvey(teamsContext, surveyId);
    }
  }, 1000);

  useEffect(() => {
    if (!survey) {
      return;
    }

    if (survey.currentQuestionId) {
      const question = survey.questions.find(
        q => q.id === survey.currentQuestionId
      ) as NominalQuestion;
      if (question && question.id !== activeQuestion?.question.id) {
        const index = survey.questions.findIndex(q => q.id === question.id);
        setActiveQuestion({ question, index });

        if (index === survey.questions.length - 1) {
          //Trigger the leaderboard
          setTimeout(
            () => setActiveQuestion({ question, index }),
            moment(question.closeDate).diff(getNow(clockSkew.current), "milliseconds") + 11000
          );
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [survey?.currentQuestionId]);

  if (!survey) {
    return <TeamsLoader isLoading loadingMessage="Loading trivia game..." />;
  }

  const nowSkewed = getNow(clockSkew.current);

  if (!activeQuestion || survey.state === "closed") {
    if (moment(survey.closeAt).add(10, "seconds").isSameOrBefore(nowSkewed)) {
      return <TriviaLeaderboard survey={survey} />;
    } else {
      return (
        <TriviaFormContentHost>
          <TriviaQuestionResults
            surveyId={survey.id!}
            question={survey.questions[survey.questions.length - 1] as NominalQuestion}
            index={survey.questions.length}
          />
        </TriviaFormContentHost>
      );
    }
  }

  if (
    activeQuestion.index === 0 &&
    moment(activeQuestion.question.openDate).isSameOrAfter(nowSkewed)
  ) {
    return (
      <TriviaFormContentHost
        timerStart={survey.deliverAt}
        timerEnd={activeQuestion.question.openDate}
        clockSkew={clockSkew.current}
      >
        <Flex column gap="gap.medium">
          <Text align="center" weight="bold" content="⏲️ Trivia game is starting soon!" />

          <Text
            content={`You will have ${moment
              .duration(survey.questionOpenDuration)
              .asSeconds()} seconds to answer each question.  Here are some ground rules:`}
          />

          <Text
            weight="bold"
            content={
              <>
                ⚖️ Keep it clean. No searching for answer!
                <br />
                👉 Remember to have fun and communicate!
                <br />
                📂 Category: {survey.description}
                <br />
                💬 {survey.questions.length} questions
              </>
            }
          />
        </Flex>
      </TriviaFormContentHost>
    );
  }

  if (moment(activeQuestion.question.openDate).isSameOrAfter(nowSkewed)) {
    return (
      <TriviaFormContentHost
        timerStart={survey.questions[activeQuestion.index - 1].closeDate}
        timerEnd={activeQuestion.question.openDate}
        clockSkew={clockSkew.current}
      >
        <Flex column gap="gap.medium">
          <TriviaQuestionResults
            surveyId={survey.id!}
            question={survey.questions[activeQuestion.index - 1] as NominalQuestion}
            index={activeQuestion.index}
          />
          {activeQuestion.index < survey.questions.length && (
            <>
              <Text content="Current Standings" align="center" weight="bold" size="large" />
              <TriviaLeaderboard survey={survey} />
            </>
          )}
        </Flex>
      </TriviaFormContentHost>
    );
  }

  return (
    <TriviaFormContentHost
      timerStart={activeQuestion.question.openDate}
      timerEnd={activeQuestion.question.closeDate}
      clockSkew={clockSkew.current}
    >
      <TriviaQuestionForm
        question={activeQuestion.question}
        index={activeQuestion.index + 1}
        response={userResponse?.responses.find(r => r.questionId === activeQuestion.question.id)}
        onSubmit={async (question, value) => {
          const result = await surveyApi.vote(
            teamsContext,
            survey.id as string,
            {
              command: TakingCommands.Submit,
              surveyId: survey.id as string,
              questionIds: [question.id],
              questionIndexId: question.id,
              [getQuestionFieldId(question.id)]: value,
              direction: "next",
              startDate: (question.openDate as Date).toISOString(),
            },
            trackingSource
          );

          if (!isError(result)) {
            setUserResponse(result.userResponse);
          } else {
            console.error(result);
          }
        }}
      />
    </TriviaFormContentHost>
  );
};

export const TriviaFormContentHost: React.FC<{
  timerStart?: Date;
  timerEnd?: Date;
  clockSkew?: number;
}> = ({ clockSkew, timerStart, timerEnd, children }) => {
  const [percentComplete, setPercentComplete] = useState<number>(0);
  const [secondsLeft, setSecondsLeft] = useState<number>(0);

  useInterval(() => {
    if (!timerEnd || !timerStart) {
      return;
    }

    const end = moment(timerEnd);

    const totalMS = end.diff(timerStart, "milliseconds");
    const nowSkewed = getNow(clockSkew);
    const millisecondsLeft = end.diff(nowSkewed, "milliseconds");

    const pc = millisecondsLeft > 0 ? 100 - (millisecondsLeft / totalMS) * 100 : 100;

    setPercentComplete(pc);

    setSecondsLeft(millisecondsLeft > 0 ? Math.ceil(millisecondsLeft / 1000) : 0);
  }, 100);

  return (
    <Flex column style={{ height: "100%" }}>
      <Flex.Item grow>
        <div style={{ overflowY: "auto" }}>{children}</div>
      </Flex.Item>
      {timerStart && timerEnd && (
        <Flex.Item push>
          <ProgressCountdownTimer secondsLeft={secondsLeft} percentComplete={percentComplete} />
        </Flex.Item>
      )}
    </Flex>
  );
};

const getNow = (skewMilliseconds?: number): moment.Moment => {
  const now = moment();
  if (skewMilliseconds === undefined) {
    return now;
  }

  return now.add(skewMilliseconds, "milliseconds");
};
